/**
 * Starter TopBar — the page-anchored top-of-content bar matching the
 * design's `.tb` pattern. Sits inside AppShell's main column.
 *
 * Layout: left slot (typically a Heading or eyebrow + heading pair), a
 * spacer, and a right slot for actions (theme toggle, search, primary CTA).
 *
 * Pure CSS-Module — no Mantine functional layer. Distinct from
 * `<PageHeader>` which is a content-area block; `<TopBar>` is the docked 64px
 * header of the fixed app-shell card — it does not scroll (the `.content` region
 * below it does).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './TopBar.module.css';

export interface TopBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Left slot — typically a greeting or page title. */
  readonly left?: ReactNode;
  /** Right slot — actions, theme toggle, etc. */
  readonly right?: ReactNode;
}

export const TopBar = forwardRef<HTMLDivElement, TopBarProps>(function TopBar(
  { left, right, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={[styles.root, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {/* Inner wrapper mirrors AppShell's `.content` (padding) + `.contentInner`
          (max-width + margin:auto): the header slots then share the exact left/right
          edges of the page content below, at every viewport width. */}
      <div className={styles.inner}>
        <div className={styles.left}>{left}</div>
        <div className={styles.right}>{right}</div>
      </div>
    </div>
  );
});
